import { jsx as _jsx } from "react/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { PtsDocumentModal } from '../../components/pts_document_modal';
import { invalidateSignedDocumentQueries } from '../../utils/invalidate_signed_document_queries';
import { getPtsCompletionDate, isDocumentVoided, isPtsDocument } from '../site_document_details.helpers';
/**
 * Corrects the signing date recorded on a completed wet-signature document, the classic
 * pencil beside the Completion date field.
 *
 * It reuses the PTS modal in `completionDateUpdateMode` rather than introducing a second
 * dialog, because the write is the same `complete_pts_document` call with an added audit
 * reason — exactly what classic's `WetSignUpload` did in this mode.
 */
export const useCompletionDateAction = ({ apiClient, accountId, getApiErrorMessage, canModifyDocument = true, }) => {
    const queryClient = useQueryClient();
    const [selectedDocument, setSelectedDocument] = useState(null);
    /**
     * Classic showed the pencil only for a wet-signature document that already has a date.
     * A voided document is excluded on top of that: the modal forces itself read-only for
     * one, so offering the pencil would open a dialog that cannot be used.
     */
    const canEditCompletionDate = useCallback((candidate) => canModifyDocument &&
        isPtsDocument(candidate) &&
        !!getPtsCompletionDate(candidate) &&
        !isDocumentVoided(candidate), [canModifyDocument]);
    const openCompletionDate = useCallback((candidate) => {
        if (canEditCompletionDate(candidate)) {
            setSelectedDocument(candidate);
        }
    }, [canEditCompletionDate]);
    const closeCompletionDate = useCallback(() => setSelectedDocument(null), []);
    const completionDateModal = useMemo(() => selectedDocument && apiClient
        ? createPortal(_jsx(PtsDocumentModal, { isOpen: true, completionDateUpdateMode: true, onClose: closeCompletionDate, signedDocId: selectedDocument._id, status: selectedDocument.ec__status, signatureDate: getPtsCompletionDate(selectedDocument), accountId: accountId, apiClient: apiClient, getApiErrorMessage: getApiErrorMessage, onComplete: () => {
                invalidateSignedDocumentQueries(queryClient, { documentId: selectedDocument._id });
                closeCompletionDate();
            } }), document.body)
        : null, [accountId, apiClient, closeCompletionDate, getApiErrorMessage, queryClient, selectedDocument]);
    return { canEditCompletionDate, openCompletionDate, completionDateModal };
};
